/**
 * Product Line bands — decisions 42 and 43 (2026-07-31).
 *
 * Channel discount and channel visibility are set per HubSpot Product Line
 * (`product_type`), not per product. This layer resolves a raw catalogue
 * product against the bands and hands the result to the pricing engine, which
 * is unchanged: it still reads `baseDiscountPct` and `resellerVisible` off the
 * product, those values just now come from the band.
 *
 * A product may override its band with the HubSpot properties
 * `wave9_base_discount_pct` / `wave9_reseller_visible` (decision 46). Blank on a
 * product — the normal case — means the band applies.
 *
 * ⚠️ Roughly a quarter of the price book has NO Product Line set (handsets,
 * accessories). Those map to the `(uncategorised)` band — a real, configurable
 * row, so the products show up in admin with a count rather than vanishing.
 * Everything fails closed: an unknown line means no discount and not visible.
 */

import type { CatalogueProduct } from './types.js'

/** The band products with a blank `product_type` fall into. */
export const UNCATEGORISED = '(uncategorised)'

export interface ProductLineBand {
  name: string
  /** 0.20 = 20% off list, before the partner's tier multiplier. */
  discountPct: number
  /** Allow-list: false means partners never see products on this line. */
  channelVisible: boolean
  sort?: number
  notes?: string | null
}

/**
 * ⚠️ `product_type` is a MULTI-SELECT enumeration in HubSpot, so a product can
 * sit on several Product Lines at once and the API returns them semicolon-
 * separated: `"Broadband;Backup Line / HA"`. Treating that string as one opaque
 * key silently invents a Product Line that matches no band, and the product
 * falls into the fail-closed hole — invisible, at zero discount, with nothing
 * obviously wrong.
 */
export function lineKeysFor(product: Pick<CatalogueProduct, 'category'>): string[] {
  const keys = (product.category ?? '')
    .split(';')
    .map((k) => k.trim())
    .filter(Boolean)
  return keys.length > 0 ? keys : [UNCATEGORISED]
}

/** The PRIMARY band key — the first line listed. Used for grouping and display. */
export function lineKeyFor(product: Pick<CatalogueProduct, 'category'>): string {
  return lineKeysFor(product)[0]
}

export function bandIndex(bands: ProductLineBand[]): Map<string, ProductLineBand> {
  return new Map(bands.map((b) => [b.name, b]))
}

/**
 * Decision 174: the Product Lines a DEAL's line set spans — the value pushed
 * to the `wave9_product_lines` deal property, which HubSpot workflows enrol on
 * ("customer ordered Telephony → create the provisioning task set").
 *
 * Only catalogue-backed lines contribute: ad-hoc lines have no product
 * identity, and a product with no Product Line set contributes nothing —
 * `(uncategorised)` is a pricing fail-closed band, not something a workflow
 * should trigger on. Sorted + deduped so the property value is stable across
 * syncs (an idempotent reconciler must not rewrite it every pass).
 */
export function dealProductLines(
  lines: Array<{ hsProductId: string | null }>,
  categoryByProductId: Map<string, string | null | undefined>,
): string[] {
  const out = new Set<string>()
  for (const l of lines) {
    if (!l.hsProductId) continue
    const category = categoryByProductId.get(l.hsProductId)
    if (!category?.trim()) continue
    for (const key of category.split(';').map((k) => k.trim()).filter(Boolean)) out.add(key)
  }
  return [...out].sort((a, b) => a.localeCompare(b))
}

/**
 * Resolve one product against the bands, returning a copy whose
 * `baseDiscountPct` and `resellerVisible` are final.
 *
 * Precedence (decision 46):
 *   1. the product's own HubSpot override, when set
 *   2. its Product Line band
 *   3. fail closed — zero discount, not visible
 *
 * ⚠️ `?? ` not `|| ` deliberately: a `0` discount override and a `false`
 * visibility override are real decisions that must beat the band. Only `null`
 * (blank in HubSpot) defers.
 *
 * A product whose line has no band at all — someone added a new Product Line in
 * HubSpot this morning — gets the safe half of both failure modes, and shows up
 * in admin as an unconfigured line.
 */
export function applyBand(
  product: CatalogueProduct,
  bands: Map<string, ProductLineBand>,
): CatalogueProduct {
  const keys = lineKeysFor(product)
  const matched = keys.map((k) => bands.get(k)).filter((b): b is ProductLineBand => b != null)

  // A product on SEVERAL Product Lines takes the most conservative reading of
  // its bands: the LOWEST discount, and visible only if EVERY one of its lines
  // is visible (and every one has a band). Both halves fail towards Wave9 —
  // under-discounting is a conversation, over-discounting is a margin leak, and
  // a product inheriting visibility from one line while another says "internal
  // only" is how something confidential reaches a partner catalogue.
  const bandDiscount = matched.length > 0 ? Math.min(...matched.map((b) => b.discountPct)) : 0
  const bandVisible = matched.length === keys.length && matched.every((b) => b.channelVisible)

  // ⚠️ Decision 49: a £0 product is never channel-visible, whatever the band or
  // the per-product override says. These are price-on-application containers
  // (excess construction charges, pro-rata adjustments, generic managed-service
  // and software SKUs) whose price is set per quote. For a partner that is
  // actively dangerous: buy price would be £0, so the reseller floor is £0 too
  // and the margin guardrail — the whole reason there is no approvals flow —
  // does nothing. Wave9 staff can still quote them, because internal mode has
  // free pricing (decision 27) and can type a real figure.
  const pricedForChannel = product.listPrice > 0

  return {
    ...product,
    baseDiscountPct: product.baseDiscountPctOverride ?? bandDiscount,
    resellerVisible: pricedForChannel && (product.resellerVisibleOverride ?? bandVisible),
  }
}

/** True when a product can never reach a partner catalogue because it has no price. */
export function isPriceOnApplication(product: Pick<CatalogueProduct, 'listPrice'>): boolean {
  return !(product.listPrice > 0)
}

/** True when a product departs from its Product Line band in either respect. */
export function hasOverride(product: CatalogueProduct): boolean {
  return product.baseDiscountPctOverride != null || product.resellerVisibleOverride != null
}

export function applyBands(products: CatalogueProduct[], bands: ProductLineBand[]): CatalogueProduct[] {
  const index = bandIndex(bands)
  return products.map((p) => applyBand(p, index))
}

export interface LineSummary extends ProductLineBand {
  /** How many active products sit on this line — the number that makes the setting real. */
  productCount: number
  /** How many of those carry a per-product override, so the band doesn't apply to them. */
  overrideCount: number
  /** True when the line appears in the price book but has no band configured. */
  unconfigured: boolean
}

/**
 * Admin view: every Product Line present in the price book OR configured as a
 * band, with product counts. Surfacing lines that exist in HubSpot but have no
 * band is the whole point — that is how a new Product Line gets noticed before
 * a partner wonders why they can't quote it.
 */
export function summariseLines(
  products: CatalogueProduct[],
  bands: ProductLineBand[],
): LineSummary[] {
  const index = bandIndex(bands)
  const counts = new Map<string, number>()
  const overrides = new Map<string, number>()

  // A multi-line product is counted against EVERY line it belongs to, so the
  // counts answer "what does changing this band affect?" rather than "who owns
  // this product?".
  for (const p of products) {
    if (!p.active) continue
    for (const key of lineKeysFor(p)) {
      counts.set(key, (counts.get(key) ?? 0) + 1)
      if (hasOverride(p)) overrides.set(key, (overrides.get(key) ?? 0) + 1)
    }
  }

  const names = new Set<string>([...counts.keys(), ...index.keys()])

  return [...names]
    .map((name) => {
      const band = index.get(name)
      return {
        name,
        discountPct: band?.discountPct ?? 0,
        channelVisible: band?.channelVisible ?? false,
        // Unconfigured lines sort FIRST: they are a to-do, and a new Product
        // Line added in HubSpot is invisible to partners until someone acts.
        sort: band?.sort ?? -1,
        notes: band?.notes ?? null,
        productCount: counts.get(name) ?? 0,
        overrideCount: overrides.get(name) ?? 0,
        unconfigured: !band,
      }
    })
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name))
}

/** Lines that exist in the price book with no band — the admin nag list. */
export function unconfiguredLines(products: CatalogueProduct[], bands: ProductLineBand[]): LineSummary[] {
  return summariseLines(products, bands).filter((l) => l.unconfigured && l.productCount > 0)
}
